import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {KairosMark} from '@/components/kairos-mark'
import type {Partner} from '@/hooks/inci/aliados/use-partners'

function CodeStamp({code}: {code: string}) {
  const {t} = useTranslation()
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm tracking-wider text-foreground" data-testid="partner-detail-code">
        {code}
      </span>
      <Button type="button" size="sm" variant="outline" onClick={copy} data-testid="partner-detail-code-copy">
        {copied ? t('partnerDetail.header.copied') : t('partnerDetail.header.copyCode')}
      </Button>
    </div>
  )
}

export function PartnerDetailHeader({partner}: {partner: Partner}) {
  const {t} = useTranslation()
  const displayName =
    partner.tipo === 'juridica' ? (partner.razon_social ?? '') : `${partner.name} ${partner.last_name}`
  const idLabel = partner.tipo === 'juridica' ? t('partners.create.nit') : t('partners.create.cedula')
  const idValue = partner.tipo === 'juridica' ? partner.nit : partner.cedula

  return (
    <div className="surface space-y-3 p-5" data-testid="partner-detail-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <KairosMark />
            <h1 className="font-heading text-xl text-foreground">{displayName}</h1>
            {partner.tipo === 'juridica' && <Tag tone="ink">{t('partners.tipo.juridica')}</Tag>}
            <Tag tone="neutral">{t(`partners.estado.${partner.active ? 'active' : 'inactive'}`)}</Tag>
          </div>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {idLabel}: <span className="text-foreground">{idValue}</span>
          </p>
        </div>
        <div className="space-y-1">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('partnerDetail.header.code')}
          </p>
          <CodeStamp code={partner.code} />
        </div>
      </div>
    </div>
  )
}
